import React, { useRef, useState } from 'react';
import { Anchor, Button, Card, FileButton, Group, Select, Stack, Table, Text } from '@mantine/core';
import type { IncidentAttachmentDetail, IncidentDetail } from '@isg/shared-types';
import {
  DOSYA_MAX_BOYUT_BYTES,
  INCIDENT_EK_TIPLERI,
  INCIDENT_KANIT_KATEGORILERI,
} from '@isg/validation';
import type { IncidentEkTipi, IncidentKanitKategorisi } from '@isg/validation';
import { useOlayEkIndir, useOlayEkSil, useOlayEkYukle, useOlayEkleri } from '../api/queries';
import { OLAY_EK_TIPI_ETIKET, OLAY_KANIT_KATEGORISI_ETIKET } from '../i18n/tr';
import { QueryStatus } from './parts';
import { KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * Kaza dosyası ekleri (R13 — F3B·S5; aksiyon ek bölümünün olay kolu).
 *
 * - Yükleme/silme düğmeleri `yetkiler.ekYonetebilir`den (SUNUCUDAN — ekran
 *   ikinci kural yazmaz); DURUM KAPISI YOK: SGK belgesi kapanıştan sonra
 *   da yüklenebilir ("sistem kovalamaz").
 * - Ek tipi DAR enum — seçim zorunlu; sağlık tipli içerik bu bölüme girmez
 *   (sunucu 422 ile de reddeder).
 */
export function OlayEkBolumu({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  const ekler = useOlayEkleri(olay.id);
  const yukle = useOlayEkYukle();
  const sil = useOlayEkSil();
  const indir = useOlayEkIndir();
  const sifirla = useRef<() => void>(null);
  // KAZA KAYDINDA dosyanın çapası SGK belgesidir; diğer olaylarda (ramak
  // kala · tehlikeli durum) eklenen şey KANITTIR — varsayılan ona göre.
  const kazaKaydi = olay.olayTuruAd === null;
  const [ekTipi, setEkTipi] = useState<IncidentEkTipi>(kazaKaydi ? 'sgk_bildirimi' : 'fotograf');
  // Kanıt KATEGORİSİ (F4·B2·2 — madde 146): tipten ayrı eksen, ZORUNLU
  // DEĞİL (O6) — boş bırakılabilir; mevcut ekler kategorisiz yaşar.
  const [kanitKategorisi, setKanitKategorisi] = useState<IncidentKanitKategorisi | null>(null);
  const [yerelHata, setYerelHata] = useState<string | null>(null);

  const yonetebilir = olay.yetkiler.ekYonetebilir;
  const hata = yerelHata ?? yukle.error?.message ?? sil.error?.message ?? null;

  return (
    <Card withBorder data-testid="olay-ek-bolumu">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>{kazaKaydi ? 'Kaza dosyası ekleri' : 'Kanıt ve ekler'}</Text>
          {yonetebilir && (
            <Group gap="xs">
              <Select
                size="xs"
                data={INCIDENT_EK_TIPLERI.map((tip) => ({
                  value: tip,
                  label: OLAY_EK_TIPI_ETIKET[tip] ?? tip,
                }))}
                value={ekTipi}
                onChange={(deger) => deger && setEkTipi(deger as IncidentEkTipi)}
                allowDeselect={false}
                data-testid="olay-ek-tipi"
              />
              <Select
                size="xs"
                placeholder="Kanıt kategorisi (opsiyonel)"
                clearable
                data={INCIDENT_KANIT_KATEGORILERI.map((kategori) => ({
                  value: kategori,
                  label: OLAY_KANIT_KATEGORISI_ETIKET[kategori] ?? kategori,
                }))}
                value={kanitKategorisi}
                onChange={(deger) => setKanitKategorisi(deger as IncidentKanitKategorisi | null)}
                data-testid="olay-ek-kanit-kategorisi"
              />
              <FileButton
                resetRef={sifirla}
                accept=".jpg,.jpeg,.png,.heic,.pdf"
                onChange={(dosya) => {
                  if (!dosya) return;
                  setYerelHata(null);
                  if (dosya.size > DOSYA_MAX_BOYUT_BYTES) {
                    setYerelHata('Dosya 10 MB sınırını aşıyor.');
                    sifirla.current?.();
                    return;
                  }
                  yukle.mutate(
                    { incidentId: olay.id, dosya, ekTipi, kanitKategorisi },
                    { onSettled: () => sifirla.current?.() },
                  );
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    data-testid="olay-ek-yukle"
                    loading={yukle.isPending}
                  >
                    Ek yükle
                  </Button>
                )}
              </FileButton>
            </Group>
          )}
        </Group>
        {/* P13 — kanıt eki kapısı: `olay_inceleme` listesi → bahçe (liste SUNUCUDAN). */}
        <KapiSebebi
          durum={olay.yetkiler.incelemeKapisi}
          nesne="Kanıt ekini"
          fiil="yükler"
          testId="olay-ek-yazamama-sebebi"
        />
        {hata && (
          <Text c={ANLAM.engel} size="sm" role="alert" data-testid="olay-ek-hata">
            {hata}
          </Text>
        )}
        <QueryStatus queries={[ekler]} />
        {ekler.isSuccess && ekler.data.length === 0 && (
          <Text size="sm" c="dimmed" data-testid="olay-ek-yok">
            Bu kayda ek yüklenmemiş.
          </Text>
        )}
        {ekler.isSuccess && ekler.data.length > 0 && (
          <Table data-testid="olay-ek-listesi">
            <Table.Tbody>
              {ekler.data.map((ek) => (
                <EkSatiri
                  key={ek.id}
                  ek={ek}
                  silinebilir={yonetebilir}
                  onIndir={() =>
                    indir.mutate({
                      incidentId: olay.id,
                      attachmentId: ek.id,
                      dosyaAdi: ek.dosyaAdi,
                    })
                  }
                  onSil={() => sil.mutate({ incidentId: olay.id, attachmentId: ek.id })}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
}

function EkSatiri({
  ek,
  silinebilir,
  onIndir,
  onSil,
}: {
  ek: IncidentAttachmentDetail;
  silinebilir: boolean;
  onIndir: () => void;
  onSil: () => void;
}): React.JSX.Element {
  return (
    <Table.Tr data-testid="olay-ek-satiri">
      <Table.Td>
        <Text size="sm" fw={500} component="span" data-testid={`olay-ek-tip-${ek.ekTipi}`}>
          {OLAY_EK_TIPI_ETIKET[ek.ekTipi] ?? ek.ekTipi}
        </Text>
        {ek.kanitKategorisi && (
          <Text size="xs" c="dimmed" data-testid="olay-ek-kanit-rozet">
            {OLAY_KANIT_KATEGORISI_ETIKET[ek.kanitKategorisi] ?? ek.kanitKategorisi}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Anchor
          component="button"
          type="button"
          size="sm"
          onClick={onIndir}
          data-testid="olay-ek-indir"
        >
          {ek.dosyaAdi}
        </Anchor>
        <Text size="xs" c="dimmed">
          {kbCinsinden(ek.boyutBytes)} · {ek.yukleyenEtiket}
        </Text>
      </Table.Td>
      <Table.Td>
        {silinebilir && (
          <Button
            size="xs"
            variant="subtle"
            color={ANLAM.engel}
            onClick={onSil}
            data-testid="olay-ek-sil"
          >
            Sil
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function kbCinsinden(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
